import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  IconButton, Badge, Tooltip, Drawer, Box, Typography, Divider,
  List, ListItemButton, ListItemIcon, ListItemText, Chip, Button,
  CircularProgress, Stack,
} from '@mui/material';
import NotificationsIcon       from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import InfoIcon                from '@mui/icons-material/Info';
import CheckCircleIcon         from '@mui/icons-material/CheckCircle';
import WarningIcon             from '@mui/icons-material/Warning';
import ErrorIcon               from '@mui/icons-material/Error';
import DoneAllIcon             from '@mui/icons-material/DoneAll';
import CampaignIcon            from '@mui/icons-material/Campaign';
import OpenInNewIcon           from '@mui/icons-material/OpenInNew';
import { Link as MuiLink }     from '@mui/material';

import { AppDispatch, RootState } from '../reduxStore/Store';
import {
  fetchNotifications, fetchUnreadCount, markAsRead, markAllAsRead,
} from '../reduxStore/NotificationSlice';
import { Notification } from '../models/models';
import { openLink } from '../utils/openLink';

const NIVEAU_ICON: Record<NonNullable<Notification['niveau']>, React.ReactNode> = {
  INFO:    <InfoIcon        color="info"    fontSize="small" />,
  SUCCESS: <CheckCircleIcon color="success" fontSize="small" />,
  WARNING: <WarningIcon     color="warning" fontSize="small" />,
  ERROR:   <ErrorIcon       color="error"   fontSize="small" />,
};

function formatRelative(iso?: string): string {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)        return 'à l\'instant';
  if (diff < 3600)      return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400)     return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 7 * 86400) return `il y a ${Math.floor(diff / 86400)} j`;
  return new Date(iso).toLocaleDateString();
}

const NotificationBell: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { notifications, unreadCount, loading } =
    useSelector((state: RootState) => state.NotificationSlice);

  const [open, setOpen] = useState(false);

  // Charger le compteur au montage (session authentifiée)
  useEffect(() => {
    dispatch(fetchUnreadCount());
  }, [dispatch]);

  // Recharger la liste à l'ouverture du panneau
  useEffect(() => {
    if (open) {
      dispatch(fetchNotifications({ limit: 30, offset: 0 }));
    }
  }, [open, dispatch]);

  /** Click sur le corps de la notif → marque lue uniquement.
   *  La navigation se fait via le lien explicite affiché en dessous. */
  const handleClickItem = (notif: Notification) => {
    if (notif.id && !notif.lu) dispatch(markAsRead(notif.id));
  };

  /** Click sur le lien explicite → marque lue + ouvre + ferme le drawer */
  const handleClickLien = (e: React.MouseEvent, notif: Notification) => {
    e.stopPropagation();   // empêche handleClickItem qui est sur le ListItemButton parent
    if (notif.id && !notif.lu) dispatch(markAsRead(notif.id));
    if (notif.lien) {
      setOpen(false);
      openLink(notif.lien, navigate);
    }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton onClick={() => setOpen(true)} sx={{ color: 'white' }}>
          <Badge badgeContent={unreadCount} color="error" max={99}>
            {unreadCount > 0 ? <NotificationsActiveIcon /> : <NotificationsIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
          <Typography variant="h6">Notifications</Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={() => dispatch(markAllAsRead())}
            >
              Tout marquer lu
            </Button>
          )}
        </Box>
        <Divider />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && notifications.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            <NotificationsIcon sx={{ fontSize: 48, opacity: 0.3 }} />
            <Typography variant="body2" sx={{ mt: 1 }}>Aucune notification</Typography>
          </Box>
        )}

        <List disablePadding>
          {notifications.map((n) => (
            <React.Fragment key={n.id}>
              <ListItemButton
                onClick={() => handleClickItem(n)}
                sx={{
                  bgcolor: n.lu ? 'transparent' : 'action.hover',
                  alignItems: 'flex-start',
                  py: 1.5,
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                  {NIVEAU_ICON[n.niveau]}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="body2" fontWeight={n.lu ? 400 : 600} sx={{ flexGrow: 1 }}>
                        {n.titre}
                      </Typography>
                      {n.user_id === null && (
                        <Tooltip title="Diffusé à tout le tenant">
                          <CampaignIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                        </Tooltip>
                      )}
                    </Stack>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" color="text.primary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                        {n.message}
                      </Typography>
                      {n.lien && (
                        <MuiLink
                          component="button"
                          type="button"
                          underline="hover"
                          onClick={(e) => handleClickLien(e, n)}
                          sx={{
                            display:    'inline-flex',
                            alignItems: 'center',
                            gap:        0.5,
                            mt:         0.75,
                            fontSize:   '0.8rem',
                            fontWeight: 500,
                          }}
                        >
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                          {n.lien}
                        </MuiLink>
                      )}
                      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {formatRelative(n.created_at)}
                        </Typography>
                        {!n.lu && <Chip label="Nouveau" size="small" color="primary" sx={{ height: 18 }} />}
                      </Stack>
                    </>
                  }
                />
              </ListItemButton>
              <Divider component="li" />
            </React.Fragment>
          ))}
        </List>
      </Drawer>
    </>
  );
};

export default NotificationBell;
